import { useAuth } from '@/lib/auth/auth';
import type {
  CreateEventFeedbackRequest,
  FriendActivityResponse,
  NotificationItem,
  NotificationType,
  NotificationsResponse,
} from '@/lib/api/apiTypes';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (surface: 'personal' | 'business', organizationID?: string | null, venueID?: string | null) =>
    [...notificationKeys.all, surface, organizationID ?? 'all-orgs', venueID ?? 'all-venues'] as const,
  current: () => notificationKeys.list('personal'),
};

export const friendActivityKeys = {
  all: ['friend-activity'] as const,
  list: (limit = 20, offset = 0) => [...friendActivityKeys.all, limit, offset] as const,
  current: () => friendActivityKeys.list(),
};

export type NotificationSurface = 'personal' | 'business';

type UseNotificationsParams = {
  surface?: NotificationSurface;
  organizationID?: string | null;
  venueID?: string | null;
};

export type PulseAlertBucketKey =
  | 'venue_updates'
  | 'event_reminders'
  | 'new_followers'
  | 'system_alerts';

const pulseAlertBucketOrder: PulseAlertBucketKey[] = [
  'venue_updates',
  'event_reminders',
  'new_followers',
  'system_alerts',
];

const pulseAlertBucketLabels: Record<PulseAlertBucketKey, string> = {
  venue_updates: 'Venue Updates',
  event_reminders: 'Event Reminders',
  new_followers: 'New Followers',
  system_alerts: 'System Alerts',
};

export function getPulseAlertBucket(type: NotificationType): PulseAlertBucketKey | null {
  switch (type) {
    case 'friend_rsvp':
      return null;
    case 'venue_new_event':
    case 'venue_event_cancelled':
    case 'venue_live_deal':
    case 'venue_announcement':
      return 'venue_updates';
    case 'going_event_starting_soon':
    case 'saved_event_starting_soon':
      return 'event_reminders';
    case 'follow_added':
      return 'new_followers';
    case 'event_comment':
    case 'host_new_event':
      return null;
    case 'weekly_digest':
    default:
      return 'system_alerts';
  }
}

export function getPulseAlertSections(items: NotificationItem[]) {
  const buckets: Record<PulseAlertBucketKey, NotificationItem[]> = {
    venue_updates: [],
    event_reminders: [],
    new_followers: [],
    system_alerts: [],
  };

  for (const item of items) {
    const bucket = getPulseAlertBucket(item.type);
    if (!bucket) {
      continue;
    }
    buckets[bucket].push(item);
  }

  return pulseAlertBucketOrder
    .map((key) => ({
      key,
      title: pulseAlertBucketLabels[key],
      data: buckets[key],
    }))
    .filter((section) => section.data.length > 0);
}

export function getUnreadPulseAlertCount(items: NotificationItem[]) {
  let count = 0;

  for (const item of items) {
    if (!item.is_read && getPulseAlertBucket(item.type)) {
      count += 1;
    }
  }

  return count;
}

export function getPulseAlertCount(items: NotificationItem[]) {
  let count = 0;

  for (const item of items) {
    if (getPulseAlertBucket(item.type)) {
      count += 1;
    }
  }

  return count;
}

export function getUnreadBusinessNotificationCount(items: NotificationItem[]) {
  let count = 0;

  for (const item of items) {
    if (!item.is_read) {
      count += 1;
    }
  }

  return count;
}

export function useNotifications(params?: UseNotificationsParams) {
  const hasSession = useAuth((state) => Boolean(state.session));
  const request = useAuth((state) => state.request);
  const surface = params?.surface ?? 'personal';
  const search = new URLSearchParams();
  search.set('surface', surface);
  if (params?.organizationID) {
    search.set('organization_id', params.organizationID);
  }
  if (params?.venueID) {
    search.set('venue_id', params.venueID);
  }
  const path = `/notifications?${search.toString()}`;

  return useQuery({
    queryKey: notificationKeys.list(surface, params?.organizationID, params?.venueID),
    enabled: hasSession,
    queryFn: () => request<NotificationsResponse>(path),
    staleTime: 60_000,
  });
}

export function useFriendActivity(limit = 20, offset = 0) {
  const hasSession = useAuth((state) => Boolean(state.session));
  const request = useAuth((state) => state.request);
  const search = new URLSearchParams();
  search.set('limit', String(limit));
  search.set('offset', String(offset));

  return useQuery({
    queryKey: friendActivityKeys.list(limit, offset),
    enabled: hasSession,
    queryFn: () => request<FriendActivityResponse>(`/activity/friends?${search.toString()}`),
    staleTime: 60_000,
  });
}

export function useBusinessNotificationUnreadCounts(organizationIDs: string[]) {
  const session = useAuth((state) => state.session);
  const request = useAuth((state) => state.request);

  const queries = useQueries({
    queries: organizationIDs.map((organizationID) => ({
      queryKey: notificationKeys.list('business', organizationID, null),
      enabled: Boolean(session) && organizationID.length > 0,
      queryFn: () =>
        request<NotificationsResponse>(`/notifications?surface=business&organization_id=${organizationID}`),
    })),
  });

  return organizationIDs.reduce<Record<string, number>>((counts, organizationID, index) => {
    counts[organizationID] = getUnreadBusinessNotificationCount(queries[index]?.data?.notifications ?? []);
    return counts;
  }, {});
}

export function useMarkNotificationRead() {
  const request = useAuth((state) => state.request);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationID: string) =>
      request<void>(`/notifications/${notificationID}/read`, {
        method: 'PATCH',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useDeleteNotification() {
  const request = useAuth((state) => state.request);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationID: string) =>
      request<void>(`/notifications/${notificationID}`, {
        method: 'DELETE',
      }),
    onMutate: async (notificationID) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });

      const previous = queryClient.getQueriesData<NotificationsResponse>({
        queryKey: notificationKeys.all,
      });
      queryClient.setQueriesData<NotificationsResponse>({ queryKey: notificationKeys.all }, (current) =>
        current
          ? {
              ...current,
              notifications: current.notifications.filter((item) => item.id !== notificationID),
            }
          : current
      );

      return { previous };
    },
    onError: (_error, _notificationID, context) => {
      for (const [queryKey, data] of context?.previous ?? []) {
        queryClient.setQueryData(queryKey, data);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkNotificationEventNotInterested() {
  const request = useAuth((state) => state.request);

  return useMutation({
    mutationFn: (eventID: string) =>
      request<void>(`/events/${eventID}/feedback`, {
        method: 'POST',
        body: JSON.stringify({
          feedback: 'not_interested',
        } satisfies CreateEventFeedbackRequest),
      }),
  });
}
